/**
 * one-stop/blocks-content — the seven remaining content blocks.
 *
 *   notice · button · faq · quote · stats · social · spacer
 *
 * (`help-contact` is the eighth; it renders the full-bleed footer band and lives
 * in `blocks-hero.tsx` beside the other chrome-adjacent blocks.)
 *
 * Config only, every one of them. Each is built from a shape the prototype
 * already has — the flat banner, the brand-tinted aside, the disclosure card, the
 * big-number cell, the chip row — so a page mixing content and live blocks reads
 * as one surface rather than as two authors.
 *
 * ⚠ As with the flow-entry blocks, `one-stop-builder`'s §4.3 table stops at the
 * nine milestone-1 types, so the content schemas are read by alias and every
 * reader is defensive. Raised in the milestone-2 reply.
 *
 * THREE REGISTRY SETTINGS THAT WERE DECLARED AND READ BY NOBODY are read here
 * now (CC 0020 finding 8): `notice.dismissible`, `notice.showIcon`,
 * `faq.multiOpen`, plus `button.newTab` and `button.variant` — the last one a
 * SPELLING difference rather than a missing feature, since this file was reading
 * `hierarchy` for the same setting. Each takes `false` as its absent-key
 * fallback, so a document authored before the setting existed renders exactly
 * what it renders today; the registry's own default reaches the wire at save
 * time and wins wherever it is present.
 */
import { useState } from "react";
import type { JSX, ReactElement, ReactNode } from "react";
import { Button, Icon } from "@v0y4ge/jrni-ui";
import {
  configBool,
  configFaqItems,
  configInt,
  configSocialLinks,
  configStats,
  configString,
  configText,
  configTextAny,
  hrefOf,
} from "./config";
import { Actions, Section, hostProps, rootClass, useBlockHost } from "./primitives";
import { uiStrings } from "./strings";
import type { BlockRenderProps } from "./types";

import { useT } from "@v0y4ge/i18n-runtime";
/* ── notice ───────────────────────────────────────────────────── */

/** ui-config — the tone vocabulary, against the design system's status colours. */
const NOTICE_TONE: Record<string, { border: string; background: string; text: string }> = {
  info: {
    border: "var(--jrni-color-surface-border)",
    background: "var(--jrni-color-surface-card-soft)",
    text: "var(--jrni-color-neutral-1)",
  },
  highlight: {
    border: "var(--jrni-color-primary-2)",
    background: "var(--jrni-color-primary-4)",
    text: "var(--jrni-color-neutral-1)",
  },
  success: {
    border: "var(--jrni-color-success-fill)",
    background: "var(--jrni-color-semantic-green-2)",
    text: "var(--jrni-color-status-success-text)",
  },
  warning: {
    border: "var(--jrni-color-warning-fill)",
    background: "var(--jrni-color-semantic-orange-2)",
    text: "var(--jrni-color-status-warning-text)",
  },
  danger: {
    border: "var(--jrni-color-danger-fill)",
    background: "var(--jrni-color-semantic-red-2)",
    text: "var(--jrni-color-status-danger-text)",
  },
};

/**
 * ui-config — `showIcon`'s glyph per tone, and it is deliberately NOT a new
 * vocabulary: it is the design system's OWN tone→icon map, lifted from `Banner`
 * (info → info, success → check, warning and danger → warn), with `highlight`
 * taking the brand tone's glyph, which is `info` there as well. Choosing
 * different glyphs here would fork what a tone MEANS between two surfaces of one
 * system, which is a worse outcome than not drawing an icon at all.
 */
const NOTICE_ICON: Record<string, (size: number) => ReactElement> = {
  info: (size) => Icon.info({ size }),
  highlight: (size) => Icon.info({ size }),
  success: (size) => Icon.check({ size }),
  warning: (size) => Icon.warn({ size }),
  danger: (size) => Icon.warn({ size }),
};

export function NoticeBlock({ block, ctx }: BlockRenderProps): JSX.Element | null {
  const t = uiStrings(ctx.locale);
  // Dismissal is this viewer's, this render's. It is NOT persisted: a notice a
  // tenant published is part of the document, and remembering that one browser
  // closed it would be a second authority over what the page says.
  const [dismissed, setDismissed] = useState(false);
  const text = configTextAny(block.config, ["text", "body", "message"], ctx.locale);
  const title = configText(block.config, "title", ctx.locale);
  if ((!text && !title) || dismissed) return null;
  const key = configString(block.config, "tone", configString(block.config, "severity", "info"));
  const tone = NOTICE_TONE[key] ?? NOTICE_TONE["info"]!;
  const href = hrefOf(block.config["link"] ?? block.config["target"], ctx.locale);
  const linkLabel = configTextAny(block.config, ["linkLabel", "ctaLabel"], ctx.locale);
  const dismissible = configBool(block.config, "dismissible", false);
  const icon = configBool(block.config, "showIcon", false)
    ? (NOTICE_ICON[key] ?? NOTICE_ICON["info"]!)(16)
    : null;

  const body: ReactNode = (
    <>
      {title ? (
        <strong style={{ display: "block", fontWeight: "var(--jrni-font-weight-bold)" }}>{title}</strong>
      ) : null}
      {text}
      {href && linkLabel ? (
        <>
          {" "}
          <a href={href}>{linkLabel}</a>
        </>
      ) : null}
    </>
  );

  return (
    <Section>
      <div
        className="osr-banner"
        style={{ borderColor: tone.border, background: tone.background, color: tone.text, margin: 0 }}
      >
        {/* The band only becomes a ROW when it has furniture to lay out. With
            neither setting on it renders exactly the flat banner it always did,
            same element, same padding, same line height. */}
        {icon !== null || dismissible ? (
          <div className="osr-banner-row">
            {icon !== null ? (
              <span className="osr-banner-icon" aria-hidden="true">
                {icon}
              </span>
            ) : null}
            <div className="osr-banner-body">{body}</div>
            {dismissible ? (
              <button
                type="button"
                className="osr-banner-dismiss"
                /* The glyph carries no text, so the NAME is the accessible name
                   and it comes from the catalog like every other word here. */
                aria-label={t("notice.dismiss")}
                title={t("notice.dismiss")}
                onClick={() => setDismissed(true)}
              >
                {Icon.x({ size: 14 })}
              </button>
            ) : null}
          </div>
        ) : (
          body
        )}
      </div>
    </Section>
  );
}

/* ── button ───────────────────────────────────────────────────── */

/**
 * A single authored link, wearing the design system's button. An authored button
 * pointing at nothing renders DISABLED with its reason rather than as a live
 * control that does nothing — the same rule every CTA in this package follows.
 */
export function ButtonBlock({ block, ctx }: BlockRenderProps): JSX.Element | null {
  const t = uiStrings(ctx.locale);
  const label = configTextAny(block.config, ["label", "ctaLabel", "text"], ctx.locale);
  if (!label) return null;
  const href = hrefOf(block.config["target"] ?? block.config["href"] ?? block.config["url"], ctx.locale);
  /**
   * ONE SETTING, TWO SPELLINGS. The registry calls it `variant`; this block was
   * reading `hierarchy`, so a builder that chose "secondary" got a primary
   * button and nothing said why (CC 0020 finding 8). Both are read now —
   * `hierarchy` first, because that is the spelling the documents we hold
   * carry — and the vocabulary is the same either way. The two values this
   * block has ever supported are the two it still supports: widening it to the
   * design system's full variant set would be guessing at an enum nobody has
   * published.
   */
  const chosen = configString(
    block.config,
    "hierarchy",
    configString(block.config, "variant", "primary"),
  );
  const hierarchy = chosen === "secondary" ? "secondary" : "primary";
  const newTab = configBool(block.config, "newTab", false);
  const title = configText(block.config, "title", ctx.locale);
  const subtitle = configText(block.config, "subtitle", ctx.locale);

  return (
    <Section title={title} subtitle={subtitle}>
      <Actions>
        <span className="osr-act jrni-action">
          <Button
            hierarchy={hierarchy}
            size="medium"
            disabled={href === null}
            title={href === null ? t("flow.unconfigured") : undefined}
            onClick={() => {
              if (!href) return;
              // `noopener,noreferrer` is not decoration on a new-tab open: without
              // it the opened page keeps a live `window.opener` handle back into
              // this one, and an authored target can point anywhere.
              if (newTab) window.open(href, "_blank", "noopener,noreferrer");
              else window.location.assign(href);
            }}
          >
            {label}
          </Button>
        </span>
      </Actions>
      {href === null ? <p className="osr-cta-reason jrni-cta-reason">{t("flow.unconfigured")}</p> : null}
    </Section>
  );
}

/* ── faq ─────────────────────────────────────────────────────── */

export function FaqBlock({ block, ctx }: BlockRenderProps): JSX.Element | null {
  const t = uiStrings(ctx.locale);
  const items = configFaqItems(block.config, ctx.locale);
  /**
   * `multiOpen` is the sharp one of the eight. Each row used to hold its own
   * open flag, so an FAQ was ALWAYS multi-open — the registry's declared default
   * (`false`) was the one behaviour the block could not do (CC 0020 finding 8).
   * The open set lives on the LIST now: with `multiOpen` off, opening a question
   * closes the others; with it on, nothing changes from what shipped.
   */
  const multiOpen = configBool(block.config, "multiOpen", false);
  const [open, setOpen] = useState<readonly number[]>([]);
  if (items.length === 0) return null;
  const title = configText(block.config, "title", ctx.locale);
  const subtitle = configText(block.config, "subtitle", ctx.locale);

  const toggle = (index: number): void =>
    setOpen((current) =>
      current.includes(index)
        ? current.filter((i) => i !== index)
        : multiOpen
          ? [...current, index]
          : [index],
    );

  return (
    <Section title={title} subtitle={subtitle}>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {items.map((item, i) => (
          <FaqItemRow
            key={`${block.id}-faq-${i}`}
            question={item.question}
            answer={item.answer}
            open={open.includes(i)}
            onToggle={() => toggle(i)}
            t={t}
          />
        ))}
      </div>
    </Section>
  );
}

function FaqItemRow(props: {
  question: string;
  answer: string | null;
  open: boolean;
  onToggle: () => void;
  t: ReturnType<typeof uiStrings>;
}): JSX.Element {
  return (
    <div className="osr-disclosure jrni-disclosure">
      <button
        type="button"
        className="osr-disclosure-head"
        aria-expanded={props.open}
        onClick={props.onToggle}
      >
        <span className="osr-row-title jrni-item-title">{props.question}</span>
        <span className="osr-disclosure-more">
          {props.open ? props.t("faq.hide") : props.t("faq.show")}
        </span>
      </button>
      {props.open && props.answer ? (
        <div className="osr-disclosure-body">
          <p className="osr-sub jrni-block-subtitle" style={{ lineHeight: 1.5 }}>
            {props.answer}
          </p>
        </div>
      ) : null}
    </div>
  );
}

/* ── quote ────────────────────────────────────────────────────── */

export function QuoteBlock({ block, ctx }: BlockRenderProps): JSX.Element | null {
  const t = useT();
  const quote = configTextAny(block.config, ["quote", "text", "body"], ctx.locale);
  if (!quote) return null;
  const attribution = configTextAny(block.config, ["attribution", "author", "cite"], ctx.locale);
  const role = configTextAny(block.config, ["role", "title"], ctx.locale);

  return (
    <Section>
      <figure className="osr-tinted" style={{ margin: 0, maxWidth: "70ch" }}>
        <blockquote
          style={{
            margin: 0,
            fontSize: "var(--jrni-font-size-P1)",
            lineHeight: 1.5,
            color: "var(--jrni-color-chrome-ink)",
            textWrap: "pretty",
          }}
        >
          {quote}
        </blockquote>
        {attribution ? (
          <figcaption className="osr-tinted-body">
            {attribution}
            {role ? t("blocks.oneStopContent.text5", "· {role}", { role }) : ""}
          </figcaption>
        ) : null}
      </figure>
    </Section>
  );
}

/* ── stats ────────────────────────────────────────────────────── */

/**
 * The prototype's big-number treatment, made into a row of cells. Values are
 * AUTHORED — this block reports nothing live and computes nothing, so it can
 * never disagree with a live block on the same page.
 *
 * `stats.emphasis` is declared in the registry and still read by nobody, and
 * that is deliberate: nothing published names its values, so implementing it
 * would mean inventing an enum and then rendering against the invention. Named
 * as declined in the 0020 reply with the ask attached — publish the vocabulary
 * and this is a one-line map.
 */
export function StatsBlock({ block, ctx }: BlockRenderProps): JSX.Element | null {
  const items = configStats(block.config, ctx.locale);
  if (items.length === 0) return null;
  const title = configText(block.config, "title", ctx.locale);
  const subtitle = configText(block.config, "subtitle", ctx.locale);

  return (
    <Section title={title} subtitle={subtitle}>
      <div className="osr-stats jrni-stats">
        {items.map((item, i) => (
          <div className="osr-stat jrni-stats-item" key={`${block.id}-stat-${i}`}>
            <span className="osr-stat-value jrni-stats-value">{item.value}</span>
            {item.label ? <span className="osr-stat-label jrni-stats-label">{item.label}</span> : null}
          </div>
        ))}
      </div>
    </Section>
  );
}

/* ── social ───────────────────────────────────────────────────── */

/**
 * `social.style` is the same case as `stats.emphasis`: declared, unpublished
 * vocabulary, so it is declined rather than guessed. What DID change here is
 * upstream — a `links[].url` carrying a deep-link target now resolves through
 * `hrefOf`, so these render as anchors instead of labels wherever the target is
 * reachable (CC 0020 finding 6).
 */
export function SocialBlock({ block, ctx }: BlockRenderProps): JSX.Element | null {
  const links = configSocialLinks(block.config, ctx.locale);
  if (links.length === 0) return null;
  const title = configText(block.config, "title", ctx.locale);
  const subtitle = configText(block.config, "subtitle", ctx.locale);

  return (
    <Section title={title} subtitle={subtitle}>
      <div className="osr-chips jrni-search-chips">
        {links.map((link, i) =>
          link.href ? (
            <a
              key={`${block.id}-social-${i}`}
              className="osr-pill-action jrni-action"
              href={link.href}
              rel="noopener noreferrer"
              target="_blank"
            >
              {link.label}
            </a>
          ) : (
            <span key={`${block.id}-social-${i}`} className="osr-pill-action jrni-action">
              {link.label}
            </span>
          ),
        )}
      </div>
    </Section>
  );
}

/* ── spacer ───────────────────────────────────────────────────── */

/**
 * Authored vertical space, bounded so a mis-typed value cannot push the rest of
 * the page off a phone. Presentational, so it is hidden from assistive tech.
 *
 * ⚠ THE REGISTRY SPELLS THIS `size`, AS AN ENUM, and this reads a numeric
 * `height` — so a builder-authored spacer collapses to the 24px default every
 * time (CC 0020 finding 8). It is left that way ON PURPOSE for one pass: mapping
 * the enum needs its VALUES, and nothing published names them. Guessing
 * small/medium/large would ship a spacer that silently renders the wrong step
 * for any vocabulary that is not the guess, which is the same defect wearing a
 * different number. Declined with the ask attached in the 0020 reply.
 */
export function SpacerBlock({ block }: BlockRenderProps): JSX.Element {
  const host = useBlockHost();
  const raw = configInt(block.config, "height", 24) ?? 24;
  const height = Math.min(Math.max(raw, 0), 160);
  return <div aria-hidden="true" className={`osr-spacer${rootClass(host)}`} style={{ height }} {...hostProps(host, "ready")} />;
}
